import {
  ContentCopyOutlined,
  DownloadOutlined,
  InfoOutlined,
  OpenInNew,
  Refresh,
  SystemUpdateAltOutlined,
  VpnKeyOutlined,
} from '@mui/icons-material'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Link,
  Paper,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { FunctionComponent, MouseEvent, ReactNode, useState } from 'react'

import { useUtils } from '../../../../../base/hooks/useUtils.tsx'
import { UseIsiPrintResult } from '../../hooks/useIsiPrint.ts'
import { detectClientOS } from '../../sdk/isi-printer-client.ts'
import IsiPrintLicenciaButton from './IsiPrintLicenciaButton'

interface Props {
  isi: UseIsiPrintResult
  /** Oculta el resumen cuando todo está bien (solo muestra avisos) */
  soloAvisos?: boolean
}

const formatoMB = (bytes?: number | null) => (bytes ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : null)
const formatoFecha = (iso?: string | null) => {
  if (!iso) return null
  const d = new Date(iso)
  return isNaN(d.getTime()) ? null : d.toLocaleDateString('es-BO')
}

/**
 * Detalle de la versión publicada (notas, fecha y SHA-256) en un popover.
 * Popover en lugar de tooltip para que también se abra en pantallas táctiles.
 */
const InfoVersion: FunctionComponent<{ isi: UseIsiPrintResult }> = ({ isi }) => {
  const { toast } = useUtils()
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const a = isi.actualizacion
  const fecha = formatoFecha(a.publishedAt)
  if (!a.notes && !fecha && !a.sha256 && !isi.manifiestoError) return null

  return (
    <>
      <Tooltip title="Detalles de la versión">
        <IconButton
          size="small"
          aria-label="Detalles de la versión"
          onClick={(e: MouseEvent<HTMLElement>) => setAnchor(e.currentTarget)}
        >
          <InfoOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { p: 2, maxWidth: 'min(360px, calc(100vw - 32px))' } } }}
      >
        <Stack spacing={1}>
          {a.latestVersion && (
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              isi-print v{a.latestVersion}
              {fecha ? ` · ${fecha}` : ''}
            </Typography>
          )}
          {a.notes && <Typography variant="body2">{a.notes}</Typography>}
          {a.sha256 && (
            <Box>
              <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="caption" color="text.secondary">
                  SHA-256 del instalador
                </Typography>
                <Tooltip title="Copiar">
                  <IconButton
                    size="small"
                    aria-label="Copiar SHA-256"
                    onClick={() =>
                      navigator.clipboard
                        ?.writeText(a.sha256!)
                        .then(() => toast.success('SHA-256 copiado'))
                        .catch(() => undefined)
                    }
                  >
                    <ContentCopyOutlined sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Typography
                variant="caption"
                component="div"
                sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
              >
                {a.sha256}
              </Typography>
            </Box>
          )}
          {isi.manifiestoError && (
            <Typography variant="caption" color="text.secondary">
              No se pudo leer la última versión publicada; el enlace abre la carpeta de descargas.
            </Typography>
          )}
        </Stack>
      </Popover>
    </>
  )
}

/**
 * El panel se adapta al ancho de su contenedor (container queries), no al de la pantalla:
 * en la columna de 320 px del diálogo de impresión se ve igual que en un móvil.
 */
const CQ_ANCHO = '@container isi-print (min-width: 480px)'

/** Botón a todo el ancho del panel; en paneles anchos, a su tamaño */
const sxBoton = { width: '100%', whiteSpace: 'normal', [CQ_ANCHO]: { width: 'auto' } } as const

/** Acciones apiladas en paneles angostos; en fila en paneles anchos */
const Acciones: FunctionComponent<{ children: ReactNode }> = ({ children }) => (
  <Stack
    spacing={1}
    useFlexGap
    sx={{
      mt: 1,
      flexDirection: 'column',
      [CQ_ANCHO]: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center' },
    }}
  >
    {children}
  </Stack>
)

/** Alert cuyo contenido usa todo el ancho (las acciones van dentro, no a la derecha) */
const sxAlert = { '& .MuiAlert-message': { flex: 1, minWidth: 0 } } as const

/** Botón de descarga del instalador publicado en latest.json */
const BotonDescarga: FunctionComponent<{ isi: UseIsiPrintResult; texto?: string }> = ({ isi, texto }) => {
  const a = isi.actualizacion
  const detalle = [a.latestVersion ? `v${a.latestVersion}` : null, formatoMB(a.size)]
    .filter(Boolean)
    .join(' · ')
  return (
    <Button
      variant="contained"
      startIcon={<DownloadOutlined />}
      sx={sxBoton}
      href={a.downloadUrl}
      target="_blank"
      rel="noopener noreferrer"
    >
      {texto ?? 'isi-print'}
      {detalle ? ` (${detalle})` : ''}
    </Button>
  )
}

/**
 * Estado de isi-print en este equipo con la acción que corresponde:
 * descargar e instalar, autorizar el sitio, resolver la licencia o actualizar.
 * Responsivo según el ancho de su contenedor.
 */
const IsiPrintEstadoPanel: FunctionComponent<Props> = ({ isi, soloAvisos = false }) => {
  const a = isi.actualizacion
  const so = detectClientOS()

  const verificar = (
    <Button
      size="small"
      color="inherit"
      variant="outlined"
      startIcon={<Refresh />}
      loading={isi.verificando}
      onClick={isi.refrescar}
      sx={sxBoton}
    >
      Verificar de nuevo
    </Button>
  )

  const contenido = (() => {
    switch (isi.estado) {
      case 'VERIFICANDO':
        return (
          <Alert severity="info" icon={<CircularProgress size={18} />}>
            Buscando isi-print en este equipo…
          </Alert>
        )

      case 'NO_SOPORTADO':
        return (
          <Alert severity="info" sx={sxAlert}>
            <AlertTitle>Impresión directa no disponible en este dispositivo</AlertTitle>
            isi-print se instala en computadoras con Windows, macOS o Linux. Desde este dispositivo puede ver,
            compartir, descargar o enviar los documentos por WhatsApp.
          </Alert>
        )

      case 'NO_INSTALADO':
        return (
          <Paper variant="outlined" sx={{ p: 1.5, borderColor: 'warning.main', [CQ_ANCHO]: { p: 2 } }}>
            <Stack spacing={1.5}>
              <Stack
                direction="row"
                spacing={1}
                sx={{ alignItems: 'center', justifyContent: 'space-between' }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700, minWidth: 0 }}>
                  Instale isi-print para imprimir desde este equipo
                </Typography>
                <InfoVersion isi={isi} />
              </Stack>
              <Acciones>
                <BotonDescarga isi={isi} />
                {verificar}
              </Acciones>
              {so.os !== 'windows' && (
                <Alert severity="info" sx={{ py: 0 }}>
                  El instalador publicado es para Windows; vea otras versiones en{' '}
                  <Link href={a.downloadUrl} target="_blank" rel="noopener noreferrer">
                    descargas
                  </Link>
                  .
                </Alert>
              )}
              <Box component="ol" sx={{ m: 0, pl: 2.5, typography: 'body2', color: 'text.secondary' }}>
                <li>Descargue y ejecute el instalador.</li>
                <li>Abra isi-print (queda junto al reloj de Windows).</li>
                <li>
                  Pulse <strong>Verificar de nuevo</strong> y autorice este sitio.
                </li>
              </Box>
            </Stack>
          </Paper>
        )

      case 'PENDIENTE_AUTORIZAR':
        return (
          <Alert severity="warning" sx={sxAlert}>
            <AlertTitle>Sitio no autorizado en isi-print</AlertTitle>
            {isi.detalle ? `${isi.detalle} ` : ''}
            Actualice la licencia si se agregó el dominio, o autorice {window.location.origin} desde el panel
            de isi-print. Se verificará automáticamente.
            <Acciones>
              <IsiPrintLicenciaButton
                isi={isi}
                color="inherit"
                variant="outlined"
                sx={sxBoton}
                texto="Autorizar con la licencia"
              />
              <Button
                size="small"
                color="inherit"
                variant="outlined"
                startIcon={<OpenInNew />}
                onClick={isi.abrirPanel}
                sx={sxBoton}
              >
                Abrir panel
              </Button>
              {verificar}
            </Acciones>
          </Alert>
        )

      case 'ACTUALIZACION_REQUERIDA':
        return (
          <Alert severity="error" icon={<SystemUpdateAltOutlined />} sx={sxAlert}>
            <AlertTitle>isi-print debe actualizarse</AlertTitle>
            Versión instalada {isi.version}; se requiere {a.minVersion} o superior.
            <Acciones>
              <BotonDescarga isi={isi} texto="Actualizar" />
            </Acciones>
          </Alert>
        )

      case 'SIN_LICENCIA':
        return (
          <Alert severity="warning" icon={<VpnKeyOutlined />} sx={sxAlert}>
            <AlertTitle>isi-print sin licencia</AlertTitle>
            {isi.detalle ?? 'No hay una licencia de impresión vigente.'}
            {isi.licenciaProductoActiva && (
              <Acciones>
                <IsiPrintLicenciaButton
                  isi={isi}
                  color="inherit"
                  variant="outlined"
                  sx={sxBoton}
                  texto="Reintentar"
                />
              </Acciones>
            )}
          </Alert>
        )

      default: {
        const dias = isi.licenciaComercio?.diasRestantes
        const porVencer = dias != null && dias <= 7
        if (soloAvisos && !a.updateAvailable && !porVencer && !isi.detalle) return null
        return (
          <Stack spacing={1}>
            {isi.detalle && <Alert severity="warning">{isi.detalle}</Alert>}
            {porVencer && (
              <Alert severity="warning">
                La licencia de impresión vence en {dias} día{dias === 1 ? '' : 's'}
                {isi.licenciaComercio?.fechaVencimiento ? ` (${isi.licenciaComercio.fechaVencimiento})` : ''}.
              </Alert>
            )}
            {a.updateAvailable && (
              <Alert severity="info" sx={sxAlert}>
                Hay una nueva versión de isi-print ({a.latestVersion}); tiene instalada la {isi.version}.
                <Acciones>
                  <BotonDescarga isi={isi} texto="Descargar" />
                </Acciones>
              </Alert>
            )}
            {!soloAvisos && (
              <Typography variant="caption" color="text.secondary">
                isi-print v{isi.version ?? '-'}
                {isi.licencia?.expires_at
                  ? ` · licencia del equipo hasta ${formatoFecha(isi.licencia.expires_at)}`
                  : ''}
                {' · '}
                <Link component="button" variant="caption" onClick={isi.abrirPanel}>
                  Abrir panel de isi-print
                </Link>
                <IsiPrintLicenciaButton isi={isi} sx={{ ml: 1, py: 0 }} />
              </Typography>
            )}
          </Stack>
        )
      }
    }
  })()

  if (!contenido) return null
  return (
    <Box sx={{ containerType: 'inline-size', containerName: 'isi-print', width: '100%' }}>{contenido}</Box>
  )
}

export default IsiPrintEstadoPanel
